import { useEffect } from 'react';

import type { CustomOfferFormValues } from '@/typings/formValues';
import type { UseFormSetValue } from 'react-hook-form';
import { isCruiseItemType } from '~/validation/schemas/customOfferValidationPredicates';

import { calculateCruisePostPurchaseTotals } from '../../calculations';
import { CRUISE_POST_PURCHASE_TYPE_REFARE, PURCHASE_TYPE_POST_PURCHASE } from '../../constants';
import type { PurchaseType } from '../../constants';
import type { CustomerPaymentSectionState } from '../../paymentFlowUtils';

type Params = {
  customerPaymentSectionState: Pick<CustomerPaymentSectionState, 'isLegacyExistingFlow'>;
  cruiselineAdditionalCharges: number;
  cruiselineFee: number;
  cruiseLeFee: number;
  itemType: string;
  postPurchaseType?: string;
  purchaseType: PurchaseType;
  refareDepositFeeAlreadyPaid: number;
  refareNewFare: number;
  refareNewTaxAndSurcharges: number;
  setValue: UseFormSetValue<CustomOfferFormValues>;
  v2PaymentDeadline?: string;
};

export function useCruisePostPurchase({
  customerPaymentSectionState,
  cruiselineAdditionalCharges,
  cruiselineFee,
  cruiseLeFee,
  itemType,
  postPurchaseType,
  purchaseType,
  refareDepositFeeAlreadyPaid,
  refareNewFare,
  refareNewTaxAndSurcharges,
  setValue,
  v2PaymentDeadline,
}: Params) {
  const isCruisePostPurchase = isCruiseItemType(itemType) && purchaseType === PURCHASE_TYPE_POST_PURCHASE;
  const isCruiseRefare = isCruisePostPurchase && postPurchaseType === CRUISE_POST_PURCHASE_TYPE_REFARE;
  const { total, vendorTotal } = calculateCruisePostPurchaseTotals({
    cruiselineAdditionalCharges,
    cruiselineFee,
    cruiseLeFee,
    refareDepositFeeAlreadyPaid: isCruiseRefare ? refareDepositFeeAlreadyPaid : 0,
    refareNewFare: isCruiseRefare ? refareNewFare : 0,
    refareNewTaxAndSurcharges: isCruiseRefare ? refareNewTaxAndSurcharges : 0,
  });

  useEffect(() => {
    if (!isCruisePostPurchase) {
      return;
    }

    if (customerPaymentSectionState.isLegacyExistingFlow) {
      setValue('payment_metadata.payment_type', 'full_payment', { shouldValidate: true });
      return;
    }

    setValue('payment_metadata.payment_flow_v2.type', 'full_payment', { shouldValidate: true });
  }, [customerPaymentSectionState.isLegacyExistingFlow, isCruisePostPurchase, setValue]);

  useEffect(() => {
    if (!isCruisePostPurchase || customerPaymentSectionState.isLegacyExistingFlow || !v2PaymentDeadline) {
      return;
    }

    setValue('due_date', v2PaymentDeadline, { shouldValidate: true });
  }, [customerPaymentSectionState.isLegacyExistingFlow, isCruisePostPurchase, setValue, v2PaymentDeadline]);

  return { isCruisePostPurchase, isCruiseRefare, total, vendorTotal };
}
